import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  COMMO_BUILD_VERSION,
  Environment,
} from '@sankarah/commo-shared-environment';
import { PublicAppUrl } from '@sankarah/shared-environment';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import { SentryUserService } from './sentry-user.service';

/** 招待コードを URL から落とす（存在や状態を Sentry に漏らさない） */
const INVITE_PATH = /\/invite\/[a-z0-9]{8}(?=$|[/?#])/gi;

export function initCommoSentry(env: Environment): void {
  const dsn = env.sentryDsn.trim();
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: sentryEnvironmentName(env),
    release: `commo@${COMMO_BUILD_VERSION}`,
    sendDefaultPii: false,
    tracesSampleRate: env.production ? 0.2 : 1,
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: [env.publicAppUrl, env.supabaseUrl],
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }
      if (typeof event.transaction === 'string') {
        event.transaction = scrubUrl(event.transaction);
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        const data = { ...breadcrumb.data };
        delete data['body'];
        delete data['request_body'];
        if (typeof data['url'] === 'string') {
          data['url'] = scrubUrl(data['url']);
        }
        if (typeof data['from'] === 'string') {
          data['from'] = scrubUrl(data['from']);
        }
        if (typeof data['to'] === 'string') {
          data['to'] = scrubUrl(data['to']);
        }
        breadcrumb.data = data;
      }
      if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = scrubUrl(breadcrumb.message);
      }
      return breadcrumb;
    },
  });
}

export function provideCommoSentry(): EnvironmentProviders {
  if (!environment.sentryDsn.trim()) {
    return makeEnvironmentProviders([]);
  }

  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ logErrors: false }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      inject(SentryUserService);
    }),
  ]);
}

export function captureBootstrapError(error: unknown): void {
  Sentry.captureException(error);
}

function sentryEnvironmentName(env: Environment): string {
  switch (env.publicAppUrl) {
    case PublicAppUrl.Production:
      return 'production';
    case PublicAppUrl.Development:
      return 'development';
    default:
      return 'localhost';
  }
}

function scrubUrl(url: string): string {
  return url.replace(INVITE_PATH, '/invite/[filtered]');
}
